import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchBooks } from '../../store/books/booksSlice';
import { BookCard } from '../../components/consumer/BookCard';
import { BookCardSkeleton, EmptyState } from '../../components/common';

export const BooksListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { books, isLoading, totalPages, page } = useAppSelector(
    (state) => state.books
  );
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    dispatch(fetchBooks({ page: 1, limit: 12, search, category: category || undefined }));
  }, [dispatch, search, category]);

  const handlePageChange = (newPage: number) => {
    dispatch(fetchBooks({ page: newPage, limit: 12, search, category: category || undefined }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  return (
    <div>
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">Browse Books</h1>
        <p className="text-sm sm:text-base text-slate-500">Discover your next great read</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6 lg:mb-8">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
          <input
            type="text"
            placeholder="Search by title or author..."
            value={search}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2.5 text-sm sm:text-base border border-primary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full sm:w-auto px-4 py-2.5 text-sm sm:text-base border border-primary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="">All Categories</option>
          <option value="Christian book">Christian book</option>
          <option value="CDF Manual">CDF Manual</option>
          <option value="Educational books">Educational books</option>
        </select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <BookCardSkeleton key={i} />
          ))}
        </div>
      ) : books.length === 0 ? (
        <EmptyState
          variant="search"
          title="No books found"
          description={search || category ? "Try adjusting your search or filter to find what you're looking for." : "There are no books in the library yet."}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
            {books.map((book, index) => (
              <div key={book._id} className="animate-scale-in" style={{ animationDelay: `${index * 50}ms` }}>
                <BookCard book={book} />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 sm:gap-4 mt-6 sm:mt-8">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-primary-700 bg-white border border-primary-200 rounded-xl hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="text-xs sm:text-sm text-primary-600 font-medium">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-primary-700 bg-white border border-primary-200 rounded-xl hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
